import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { CloudIcon } from '@heroicons/react/24/outline';

export type BinaryInactiveReason =
  | 'provider_mismatch'
  | 'owner_trial_cloud'
  | 'owner_auth_invalid'
  | 'collaborator_unlinked'
  | 'collaborator_auth_invalid'
  | 'acl_failed'
  | string;

interface BinaryUnavailablePlaceholderProps {
  reason?: BinaryInactiveReason;
  requiredProvider?: string;
  viewerRole?: 'owner' | 'collaborator';
  className?: string;
}

const providerLabel = (provider: string, t: (k: string, d?: string) => string) =>
  t(`storage.providers.${provider}`, provider);

const BinaryUnavailablePlaceholder: React.FC<BinaryUnavailablePlaceholderProps> = ({
  reason = 'provider_mismatch',
  requiredProvider,
  viewerRole = 'collaborator',
  className = '',
}) => {
  const { t } = useTranslation();

  const bodyKey =
    reason === 'owner_trial_cloud'
      ? 'sharing.binaryUnavailable.body.ownerTrialCloud'
      : reason === 'owner_auth_invalid'
        ? 'sharing.binaryUnavailable.body.ownerAuthInvalid'
        : reason === 'collaborator_unlinked'
          ? 'sharing.binaryUnavailable.body.collaboratorUnlinked'
          : reason === 'collaborator_auth_invalid'
            ? 'sharing.binaryUnavailable.body.collaboratorAuthInvalid'
            : reason === 'acl_failed'
              ? 'sharing.binaryUnavailable.body.aclFailed'
              : 'sharing.binaryUnavailable.body.providerMismatch';

  const providerName = requiredProvider ? providerLabel(String(requiredProvider), t) : t('sharing.binaryUnavailable.yourCloud');
  const showReconnectCta =
    (viewerRole === 'owner' && (reason === 'owner_auth_invalid' || reason === 'owner_trial_cloud')) ||
    (viewerRole === 'collaborator' &&
      requiredProvider &&
      (reason === 'collaborator_unlinked' ||
        reason === 'collaborator_auth_invalid' ||
        reason === 'provider_mismatch'));

  return (
    <div
      className={`rounded-lg border border-dashed border-[var(--color-border)] bg-[var(--color-hover)]/40 p-6 text-center ${className}`}
    >
      <CloudIcon className="mx-auto h-10 w-10 text-[var(--color-muted)] opacity-60" />
      <p className="mt-3 text-sm font-medium text-[var(--color-text)]">
        {t('sharing.binaryUnavailable.title', 'Files not available')}
      </p>
      <p className="mt-2 text-xs text-[var(--color-muted)] max-w-md mx-auto">
        {t(bodyKey, {
          provider: providerName,
          defaultValue:
            viewerRole === 'owner'
              ? `Collaborators need the same cloud provider (${providerName}) to access files.`
              : `Connect ${providerName} in Settings to access project files.`,
        })}
      </p>
      {showReconnectCta && (
        <Link
          to="/settings"
          className="inline-block mt-4 text-xs font-medium text-[var(--brand-from)] hover:underline"
        >
          {t('sharing.binaryUnavailable.cta.reconnectProvider', {
            provider: providerName,
            defaultValue: `Reconnect ${providerName}`,
          })}
        </Link>
      )}
    </div>
  );
};

export default BinaryUnavailablePlaceholder;
